import React, { useState, useEffect } from 'react';
import { fetchAllData } from '../../helpers/externapi';
import * as XLSX from 'xlsx';

const DashboardLeaderBoardSummery = () => {
    const [leaderData, setLeaderData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sortConfig, setSortConfig] = useState({
        key: 'AccountsActivatedYTD',
        direction: 'desc'
    });
    const [visibleCount, setVisibleCount] = useState(10);

    const PAGE_SIZE = 10;

    const today = new Date();
    const currentMonth = today.toLocaleString('default', { month: 'short' });
    const currentYear = today.getFullYear();

    const timeLabels = ['Today', currentMonth, currentYear];

    useEffect(() => {
        const fetchLeaderBoard = async () => {
            try {
                setLoading(true);
                const data = await fetchAllData('lambdaAPI/Employee/GetLeaderBoardSummary');
                if (data && data.status && data.data) {
                    setLeaderData(data.data);
                } else if (Array.isArray(data)) {
                    setLeaderData(data);
                }
            } catch (error) {
                console.error('Error fetching leaderboard data:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchLeaderBoard();
    }, []);

    const getSortedData = () => {
        return [...leaderData].sort((a, b) => {
            const aVal = a[sortConfig.key] || 0;
            const bVal = b[sortConfig.key] || 0;
            return sortConfig.direction === 'desc' ? bVal - aVal : aVal - bVal;
        });
    };

    const handleSort = (colKey) => {
        setSortConfig((prev) => ({
            key: colKey,
            direction: prev.key === colKey && prev.direction === 'desc' ? 'asc' : 'desc',
        }));
        setVisibleCount(PAGE_SIZE);
    };

    const getSortIcon = (colKey) => {
        if (sortConfig.key !== colKey)
            return <i className="fa-solid fa-sort ms-1" style={{ fontSize: 10, color: '#ccc' }}></i>;
        return sortConfig.direction === 'desc'
            ? <i className="fa-solid fa-sort-down ms-1" style={{ fontSize: 10, color: '#4287f5' }}></i>
            : <i className="fa-solid fa-sort-up ms-1" style={{ fontSize: 10, color: '#4287f5' }}></i>;
    };

    // ─── Excel Download ───────────────────────────────────────────────────────
    const handleDownloadExcel = () => {
        const sortedData = getSortedData();

        // Row 1: group headers (merged cells)
        // Row 2: sub-column headers
        // Rows 3+: data

        const wb = XLSX.utils.book_new();
        const ws = {};

        // Helper to set a cell
        const setCell = (ws, col, row, value, bold = false) => {
            const ref = XLSX.utils.encode_cell({ c: col, r: row });
            ws[ref] = {
                v: value,
                t: typeof value === 'number' ? 'n' : 's',
                s: {
                    font: { bold },
                    alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
                    border: {
                        top: { style: 'thin', color: { rgb: 'CCCCCC' } },
                        bottom: { style: 'thin', color: { rgb: 'CCCCCC' } },
                        left: { style: 'thin', color: { rgb: 'CCCCCC' } },
                        right: { style: 'thin', color: { rgb: 'CCCCCC' } },
                    },
                    fill: bold ? { fgColor: { rgb: 'F8F9FA' } } : undefined,
                },
            };
        };

        // ── Row 0: group headers ──
        // Static cols: Sl No(0), Name(1), Designation(2), Date of Joining(3)
        setCell(ws, 0, 0, 'Sl No', true);
        setCell(ws, 1, 0, 'Name', true);
        setCell(ws, 2, 0, 'Designation', true);
        setCell(ws, 3, 0, 'Date of Joining', true);
        // Group headers span 3 cols each
        setCell(ws, 4, 0, 'Advisors Onboard', true);
        setCell(ws, 7, 0, 'Advisors Active', true);
        setCell(ws, 10, 0, 'Accounts Activated', true);

        // ── Row 1: sub-column headers ──
        const subLabels = [...timeLabels, ...timeLabels, ...timeLabels];
        subLabels.forEach((label, i) => setCell(ws, 4 + i, 1, String(label), true));

        // Static cols row 1 (blank, since row 0 spans with merge)
        setCell(ws, 0, 1, '');
        setCell(ws, 1, 1, '');
        setCell(ws, 2, 1, '');
        setCell(ws, 3, 1, '');

        // ── Rows 2+: data ──
        leaderData.forEach((row, idx) => {
            const r = idx + 2;
            setCell(ws, 0, r, row['Sl No'] ?? idx + 1);
            setCell(ws, 1, r, row.Name || '');
            setCell(ws, 2, r, row.Designation || '');
            setCell(ws, 3, r, row.DateOfJoining || '');
            setCell(ws, 4, r, row.AdvisorsOnboardFTD ?? 0);
            setCell(ws, 5, r, row.AdvisorsOnboardMTD ?? 0);
            setCell(ws, 6, r, row.AdvisorsOnboardYTD ?? 0);
            setCell(ws, 7, r, row.AdvisorsActiveFTD ?? 0);
            setCell(ws, 8, r, row.AdvisorsActiveMTD ?? 0);
            setCell(ws, 9, r, row.AdvisorsActiveYTD ?? 0);
            setCell(ws, 10, r, row.AccountsActivatedFTD ?? 0);
            setCell(ws, 11, r, row.AccountsActivatedMTD ?? 0);
            setCell(ws, 12, r, row.AccountsActivatedYTD ?? 0);
        });

        // ── Merges: static cols span 2 header rows; group headers span 3 cols ──
        ws['!merges'] = [
            // Static columns — merge row 0 and row 1
            { s: { r: 0, c: 0 }, e: { r: 1, c: 0 } }, // Sl No
            { s: { r: 0, c: 1 }, e: { r: 1, c: 1 } }, // Name
            { s: { r: 0, c: 2 }, e: { r: 1, c: 2 } }, // Designation
            { s: { r: 0, c: 3 }, e: { r: 1, c: 3 } }, // Date of Joining
            // Group headers — each spans 3 columns in row 0
            { s: { r: 0, c: 4 }, e: { r: 0, c: 6 } }, // Advisors Onboard
            { s: { r: 0, c: 7 }, e: { r: 0, c: 9 } }, // Advisors Active
            { s: { r: 0, c: 10 }, e: { r: 0, c: 12 } }, // Accounts Activated
        ];

        // ── Column widths ──
        ws['!cols'] = [
            { wch: 6 }, // Sl No
            { wch: 20 }, // Name
            { wch: 22 }, // Designation
            { wch: 16 }, // Date of Joining
            { wch: 10 }, { wch: 10 }, { wch: 10 }, // Advisors Onboard
            { wch: 10 }, { wch: 10 }, { wch: 10 }, // Advisors Active
            { wch: 10 }, { wch: 10 }, { wch: 10 }, // Accounts Activated
        ];

        // ── Row heights ──
        ws['!rows'] = [{ hpt: 28 }, { hpt: 22 }];

        // ── Sheet range ──
        ws['!ref'] = XLSX.utils.encode_range({
            s: { r: 0, c: 0 },
            e: { r: leaderData.length + 1, c: 12 },
        });

        const sheetName = 'Leader Board Summary';
        XLSX.utils.book_append_sheet(wb, ws, sheetName);

        const fileName = `LeaderBoardSummary_${today.toISOString().slice(0, 10)}.xlsx`;
        XLSX.writeFile(wb, fileName);
    };
    // ─────────────────────────────────────────────────────────────────────────

    const skeletonStyles = `
        .lb-shimmer {
            animation-duration: 2.5s;
            animation-fill-mode: forwards;
            animation-iteration-count: infinite;
            animation-name: lbShimmer;
            animation-timing-function: linear;
            background: linear-gradient(to right, #e0e0e0 8%, #f0f0f0 18%, #e0e0e0 33%);
            background-size: 1000px 104px;
            border-radius: 4px;
        }
        @keyframes lbShimmer {
            0%   { background-position: -1000px 0; }
            100% { background-position:  1000px 0; }
        }
        .lb-th-sortable { cursor: pointer; user-select: none; white-space: nowrap; }
        .lb-th-sortable:hover { background-color: rgba(0,0,0,0.04); }
        .lb-thead-group th {
            text-align: center; background-color: #f8f9fa;
            font-size: 12px; font-weight: 700; color: #555;
            border-bottom: 2px solid #dee2e6; padding: 8px 6px;
        }
        .lb-thead-sub th {
            text-align: center; background-color: #f8f9fa;
            font-size: 11px; font-weight: 600; color: #777; padding: 6px 8px;
        }
        .lb-thead-group th:first-child,
        .lb-thead-group th:nth-child(2),
        .lb-thead-group th:nth-child(3),
        .lb-thead-group th:nth-child(4) { text-align: left; }
        .lb-cell-center { text-align: center; }
        .lb-cell-value  { font-weight: 600; color: #333; }
        .lb-cell-zero   { color: #bbb; }
        .lb-show-more-row td {
            background-color: #f8f9fa; text-align: center;
            padding: 10px; border-top: 2px solid #dee2e6;
        }
        .lb-show-more-btn {
            background: none; border: none; color: #4287f5;
            font-size: 13px; font-weight: 600; cursor: pointer; padding: 0;
        }
        .lb-show-more-btn:hover { text-decoration: underline; }
        .lb-show-less-btn {
            background: none; border: none; color: #888;
            font-size: 13px; font-weight: 600; cursor: pointer;
            padding: 0; margin-left: 16px;
        }
        .lb-show-less-btn:hover { text-decoration: underline; color: #555; }
        .lb-count-badge { font-size: 12px; color: #999; margin-left: 8px; }
    `;

    const sortedData = getSortedData();
    const visibleData = leaderData.slice(0, visibleCount);
    const totalCount = leaderData.length;
    const hasMore = visibleCount < totalCount;
    const hasLess = visibleCount > PAGE_SIZE;

    const skeletonRows = () =>
        [...Array(PAGE_SIZE)].map((_, i) => (
            <tr key={i}>
                <td><div className="lb-shimmer" style={{ width: 20, height: 14 }}></div></td>
                <td><div className="lb-shimmer" style={{ width: 110, height: 14 }}></div></td>
                <td><div className="lb-shimmer" style={{ width: 130, height: 14 }}></div></td>
                <td><div className="lb-shimmer" style={{ width: 80, height: 14 }}></div></td>
                {[...Array(9)].map((_, j) => (
                    <td key={j} className="lb-cell-center">
                        <div className="lb-shimmer" style={{ width: 28, height: 14, margin: '0 auto' }}></div>
                    </td>
                ))}
            </tr>
        ));

    const renderValue = (val) => {
        if (val === null || val === undefined || val === 0)
            return <span className="lb-cell-zero">0</span>;
        return <span className="lb-cell-value">{val}</span>;
    };

    return (
        <div className="card mt-4" style={{ zIndex: 1 }}>
            <style>{skeletonStyles}</style>

            <div className="card-header align-items-center d-flex justify-content-between">
                <h5 className="card-action-title mb-0">Leader Board Summary</h5>
                <button
                    className="btn btn-sm btn-outline-success"
                    onClick={handleDownloadExcel}
                    disabled={loading || leaderData.length === 0}
                    title="Download as Excel"
                >
                    <i className="fa-solid fa-file-excel me-1"></i>
                    Download Excel
                </button>
            </div>

            <div className="card-body p-0">
                <div className="table-responsive">
                    <table className="table table-bordered mb-0" style={{ fontSize: 13 }}>
                        <thead>
                            <tr className="lb-thead-group">
                                <th rowSpan={2} className="align-middle">Sl No</th>
                                <th rowSpan={2} className="align-middle">Name</th>
                                <th rowSpan={2} className="align-middle">Designation</th>
                                <th rowSpan={2} className="align-middle">Date of Joining</th>
                                <th colSpan={3} style={{ textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>
                                    Advisors Onboard
                                </th>
                                <th colSpan={3} style={{ textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>
                                    Advisors Active
                                </th>
                                <th colSpan={3} style={{ textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>
                                    Accounts Activated
                                </th>
                            </tr>
                            <tr className="lb-thead-sub">
                                {timeLabels.map((label, index) => (
                                    <th key={`onboard-${index}`} className="lb-cell-center">{label}</th>
                                ))}
                                {timeLabels.map((label, index) => (
                                    <th key={`active-${index}`} className="lb-cell-center">{label}</th>
                                ))}
                                {timeLabels.map((label, index) => (
                                    <th key={`accounts-${index}`} className="lb-cell-center">{label}</th>
                                ))}
                            </tr>
                        </thead>

                        <tbody>
                            {loading ? (
                                skeletonRows()
                            ) : leaderData.length === 0 ? (
                                <tr>
                                    <td colSpan={13} className="text-center py-4 text-muted">
                                        No leaderboard data available
                                    </td>
                                </tr>
                            ) : (
                                <>
                                    {visibleData.map((row, index) => (
                                        <tr key={row.EmployeeId || index}>
                                            <td>{row['Sl No'] ?? index + 1}</td>
                                            <td style={{ whiteSpace: 'nowrap' }}>{row.Name || '—'}</td>
                                            <td style={{ whiteSpace: 'nowrap' }}>{row.Designation || '—'}</td>
                                            <td className="lb-cell-center" style={{ whiteSpace: 'nowrap' }}>
                                                {row.DateOfJoining || '—'}
                                            </td>
                                            <td className="lb-cell-center">{renderValue(row.AdvisorsOnboardFTD)}</td>
                                            <td className="lb-cell-center">{renderValue(row.AdvisorsOnboardMTD)}</td>
                                            <td className="lb-cell-center">{renderValue(row.AdvisorsOnboardYTD)}</td>
                                            <td className="lb-cell-center">{renderValue(row.AdvisorsActiveFTD)}</td>
                                            <td className="lb-cell-center">{renderValue(row.AdvisorsActiveMTD)}</td>
                                            <td className="lb-cell-center">{renderValue(row.AdvisorsActiveYTD)}</td>
                                            <td className="lb-cell-center">{renderValue(row.AccountsActivatedFTD)}</td>
                                            <td className="lb-cell-center">{renderValue(row.AccountsActivatedMTD)}</td>
                                            <td className="lb-cell-center">{renderValue(row.AccountsActivatedYTD)}</td>
                                        </tr>
                                    ))}

                                    {(hasMore || hasLess) && (
                                        <tr className="lb-show-more-row">
                                            <td colSpan={13}>
                                                {hasMore && (
                                                    <button
                                                        className="lb-show-more-btn"
                                                        onClick={() => setVisibleCount(prev => prev + PAGE_SIZE)}
                                                    >
                                                        <i className="fa-solid fa-chevron-down me-1" style={{ fontSize: 11 }}></i>
                                                        Show more
                                                        <span className="lb-count-badge">
                                                            ({visibleCount} of {totalCount} shown)
                                                        </span>
                                                    </button>
                                                )}
                                                {hasLess && (
                                                    <button
                                                        className="lb-show-less-btn"
                                                        onClick={() => setVisibleCount(PAGE_SIZE)}
                                                    >
                                                        <i className="fa-solid fa-chevron-up me-1" style={{ fontSize: 11 }}></i>
                                                        Show less
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    )}
                                </>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="card-footer py-2">
                <small className="text-muted">
                    Today = Current Day · {currentMonth} = Current Month · {currentYear} = Current Year
                    {!loading && totalCount > 0 && (
                        <span> &nbsp;·&nbsp; Total: <strong>{totalCount}</strong> records</span>
                    )}
                </small>
            </div>
        </div>
    );
};

export default DashboardLeaderBoardSummery;